import { birthday, name } from 'Data/validate.js'
import { useMutation, useClient } from 'Data/Api.js'
import {
  minutesToThreeDigitsTime,
  parseLocalDateStringToZonedDate,
  patientName,
} from 'Data/format.js'
import { normalizePath, useSetFlowTo } from 'Simple/Flow.js'
import { useDataSubmit, useDataValue } from 'Simple/Data'
import { isNil, omitBy } from 'lodash'
import {
  notifyError,
  notifySuccess,
  useNotifications,
} from 'Logic/Notifications.js'
import mutationScheduleNewPatient from './mutation-schedule-new-patient.graphql.js'
import mutationScheduleExistingPatientAndNewAppointment from './mutation-schedule-existing-patient-and-new-appointment.graphql.js'
import mutationScheduleExistingPatientAndExistingAppointment from './mutation-schedule-existing-patient-and-existing-appointment.graphql.js'
import mutationCreatePatient from './mutation-create-patient.graphql.js'
import mutationCreateEmployeeProfessionalRelationship from './mutation-create-employee-professional-relationship.graphql.js'
import queryNewPatient from './query-new-patient.graphql.js'
import queryPersonMatches from './query-person-matches.graphql.js'
import { DEFAULT_TIMEZONE } from 'Data/constants.js'

/** @type {import('Simple/types.js').useDataOnSubmit} */
export default function useDataOnSubmit(props, data) {
  let onActionSchedule = useDataOnActionSchedule(props, data)
  let onActionUpdatePatientOption = useDataOnActionUpdatePatientOption(
    props,
    data
  )
  let onActionSetPatient = useDataOnActionSetPatient(props, data)
  let onActionSetNewAppointment = useDataOnActionSetNewAppointment(props, data)
  let onActionSetExistingAppointment = useDataOnActionSetExistingAppointment(
    props,
    data
  )
  let onActionSetAppointmentTemplate = useDataOnActionSetAppointmentTemplate(
    props,
    data
  )
  let onActionUpdateReferral = useDataOnActionUpdateReferral(props, data)
  let onActionCreatePatient = useDataOnActionCreatePatient(props, data)

  return async function onSubmit(params) {
    let { args } = params
    switch (args?.type) {
      case 'schedule': {
        return onActionSchedule(params)
      }
      case 'updatePatientOption': {
        return onActionUpdatePatientOption(params)
      }
      case 'setPatient': {
        return onActionSetPatient(params)
      }
      case 'setNewAppointment': {
        return onActionSetNewAppointment(params)
      }
      case 'setExistingAppointment': {
        return onActionSetExistingAppointment(params)
      }
      case 'setAppointmentTemplate': {
        return onActionSetAppointmentTemplate(params)
      }
      case 'updateReferrals': {
        return onActionUpdateReferral(params)
      }
      case 'createPatient': {
        return onActionCreatePatient(params)
      }
      default: {
        console.error(`Unsupported action ${args?.type}`)
      }
    }
  }
}

/** @type {import('Simple/types.js').useDataOnSubmit} */
function useDataOnActionSetNewAppointment(props) {
  let submitTab = useDataSubmit({
    viewPath: props.viewPath,
    context: 'tab',
  })

  return async function onAction({ args, change }) {
    change(next => {
      next.appointment_id = 'new'
      next.is_external_appointment_id = false
      next.appointment_type_id = null
      next.appointment_type_name = null
      next.template_id = null
      next.duration = null
    })

    let optimal_date = args.approximateDate ?? null
    let date = optimal_date
    submitTab({
      type: 'scheduling/reset',
      optimal_date,
      ...(date && { date }),
    })
  }
}

/** @type {import('Simple/types.js').useDataOnSubmit} */
function useDataOnActionSetExistingAppointment(props) {
  let submitTab = useDataSubmit({
    viewPath: props.viewPath,
    context: 'tab',
  })
  let timeZoneId = useDataValue({
    context: 'tab',
    path: 'selected.time_zone_id',
    viewPath: props.viewPath,
  })

  return async function onAction({ value, args, originalValue, change }) {
    let optimal_date = args.approximateDate ?? null
    let location = args.location
      ? {
          location_id: args.location.id,
          vaxiom_location_id: args.location.vaxiom_id,
          time_zone_id: args.location.time_zone_id || DEFAULT_TIMEZONE,
          location_name: args.location.name,
        }
      : null
    let date = args.booking
      ? parseLocalDateStringToZonedDate(
          args.booking.date,
          location?.time_zone_id || timeZoneId
        )
      : optimal_date

    // We show the blue bar if the appointment is an unscheduled one
    await submitTab({
      type: 'scheduling/reset',
      optimal_date,
      ...(date && { date }),
      ...(location && { location, appointment_id: args.id }),
    })

    change(next => {
      next.patient_id = args.patient_id || value.patient_id
      next.patient_option = 'existing'
      next.appointment_id = args.id
      next.appointment_type_id = args.type_id
      next.appointment_type_name = args.type_name
      next.treatment_id = args.treatment_id
      next.duration = args.duration
      next.notes = args.notes
      next.booking = args.booking || originalValue.booking
      next.is_external_any = Boolean(args.is_external)
      next.is_external_patient_id = Boolean(args.is_external)
      next.is_external_appointment_id = Boolean(args.is_external)
      next.is_external_treatment_id = Boolean(args.is_external)
    })
  }
}

/** @type {import('Simple/types.js').useDataOnSubmit} */
function useDataOnActionSetAppointmentTemplate(props) {
  return async function onAction({ value, args, change }) {
    change(next => {
      if (args.template_exists_in_location) {
        next.appointment_type_id = args.appointment_type_id
        next.appointment_type_name = args.appointment_type_name
        next.template_id = args.template_id
        if (
          !value.appointment_id ||
          value.appointment_id === 'new' ||
          !value.booking?.date
        )
          next.duration = args.duration
        next.template_exists_in_location = true
      } else {
        next.template_exists_in_location = false
      }
    })
  }
}

/** @type {import('Simple/types.js').useDataOnSubmit} */
function useDataOnActionSetPatient(props) {
  let submitTab = useDataSubmit({
    viewPath: props.viewPath,
    context: 'tab',
  })

  return async function onAction({ value, change, args }) {
    change(next => {
      next.patient_id = args.patient_id
      next.appointment_id = null
      next.treatment_id = null
      next.note = ''
      next.add_notes = false
      next.is_external_any = false
      next.is_external_patient_id = false
      next.is_external_appointment_id = false
      next.is_external_treatment_id = false
      if (!value.is_emergency) {
        next.appointment_type_id = null
        next.appointment_type_name = ''
        next.duration = null
      }
    })
    submitTab({
      type: 'scheduling/reset',
      optimal_date: null,
    })
  }
}

/** @type {import('Simple/types.js').useDataOnSubmit} */
function useDataOnActionUpdatePatientOption(props) {
  let submitTab = useDataSubmit({
    viewPath: props.viewPath,
    context: 'tab',
  })

  return async function onAction({ args, value, change }) {
    change(next => {
      next.patient_option = args.patient_option
      next.patient_id = null
      next.appointment_id = null
      next.treatment_id = null
      next.note = ''
      next.add_notes = false
      next.is_external_any = false
      next.is_external_patient_id = false
      next.is_external_appointment_id = false
      next.is_external_treatment_id = false
      if (!value.is_emergency) {
        next.appointment_type_id = null
        next.appointment_type_name = ''
        next.duration = null
      }
    })
    // We would like to reset the blue bar configuration here and not show them
    // So that when another appointment is selected eventually, the decision
    // could be made there, if we should blue bars or not.
    submitTab({
      type: 'scheduling/reset',
      optimal_date: null,
    })
  }
}

/**
 * @param {*} input
 * @returns {string | null}
 */
function validateNewPatient(input) {
  let { first_name, last_name, gender, birth_date } = input.person

  if (!name(first_name)) return 'Invalid first name'
  if (!name(last_name)) return 'Invalid last name'
  if (!gender) return 'Invalid gender'
  if (!birthday(birth_date)) return 'Invalid birth date'

  return null
}

/**
 * @param {Array} referrals
 * @returns {template_referrals: Array, person_referrals: Array}
 */
function formatReferrals(referrals) {
  let template_referrals = []
  let person_referrals = []
  referrals?.forEach(referral => {
    switch (referral.template.type) {
      case 'DropDown':
        template_referrals.push({
          referral_template_id: referral.template.id,
          text_value:
            referral.referral_list_values.find(
              v => v.id === referral.selected_index
            ).text ?? '',
        })
        break
      case 'FreeText':
        template_referrals.push({
          referral_template_id: referral.template.id,
          text_value: referral.text_value,
        })
        break
      case 'Search':
        person_referrals.push({
          person_type: referral.template.text,
          person_id: referral.person.id,
        })
        break
      default:
        break
    }
  })

  return { template_referrals, person_referrals }
}

/** @type {import('Simple/types.js').useDataOnSubmit} */
function useDataOnActionSchedule(props) {
  let client = useClient()
  let [, notify] = useNotifications()
  let setFlowTo = useSetFlowTo(props.viewPath)
  let [, executeMutationScheduleNewPatient] = useMutation(
    mutationScheduleNewPatient
  )
  let [, executeMutationScheduleExistingPatientAndNewAppointment] = useMutation(
    mutationScheduleExistingPatientAndNewAppointment
  )
  let [, executeMutationScheduleExistingPatientAndExistingAppointment] =
    useMutation(mutationScheduleExistingPatientAndExistingAppointment)

  let location_id = useDataValue({
    context: 'tab',
    path: 'selected.location_id',
    viewPath: props.viewPath,
  })
  let slots = useDataValue({
    context: 'tab',
    path: 'scheduling.slots',
    viewPath: props.viewPath,
  })
  let slot_id = useDataValue({
    context: 'tab',
    path: 'scheduling.slot_id',
    viewPath: props.viewPath,
  })
  let untemplated_slot = useDataValue({
    context: 'tab',
    path: 'scheduling.untemplated_slot',
    viewPath: props.viewPath,
  })
  let submitTab = useDataSubmit({
    context: 'tab',
    viewPath: props.viewPath,
  })
  let submitGlobal = useDataSubmit({
    context: 'global',
    viewPath: props.viewPath,
  })

  return async function onAction({ value, args }) {
    let slot = slots.find(v => v.id === slot_id)
    let date = slot?.date || untemplated_slot?.date
    let start_time =
      slot?.start_time ||
      (typeof untemplated_slot?.start_min === 'number'
        ? minutesToThreeDigitsTime(untemplated_slot.start_min)
        : null)
    let end_time =
      slot?.end_time ||
      (typeof untemplated_slot?.end_min === 'number'
        ? minutesToThreeDigitsTime(untemplated_slot.end_min)
        : null)
    let chair_id = slot?.chair_id || untemplated_slot?.chair_id

    if (!date) {
      notify(notifyError('Date not selected'))
      return true
    }

    if (!start_time) {
      notify(notifyError('Start time not selected'))
      return true
    }

    if (!end_time) {
      notify(notifyError('End time not selected'))
      return true
    }

    if (!chair_id) {
      notify(notifyError('Chair not selected'))
      return true
    }

    let {
      is_emergency,
      appointment_id,
      treatment_id,
      appointment_type_id,
      note,
      add_notes,
    } = value

    let patient_id = value.patient_id || args.patient_id

    let shared = {
      location_id,
      ...(add_notes ? { note } : {}),
      date,
      chair_id,
      start_time,
      end_time,
      appointment_type_id,
    }

    let mutationResponse

    let is_new_appointment = appointment_id === 'new' || appointment_id === null

    if (patient_id) {
      if (!is_new_appointment) {
        mutationResponse =
          await executeMutationScheduleExistingPatientAndExistingAppointment({
            ...shared,
            patient_id,
            appointment_id,
          })
      } else {
        mutationResponse =
          await executeMutationScheduleExistingPatientAndNewAppointment({
            ...shared,
            patient_id,
            treatment_id,
            unplanned: is_emergency,
          })
      }
    } else {
      let validationResult = validateNewPatient(value.patient_new)

      if (validationResult) {
        notify(notifyError(validationResult))
        return true
      }

      if (args.check_existing) {
        let response = await client
          .query(queryPersonMatches, {
            first_name: `%${value.patient_new.person.first_name.trim()}%`,
            last_name: `%${value.patient_new.person.last_name.trim()}%`,
            birth_date: value.patient_new.person.birth_date,
            birth_date_s: value.patient_new.person.birth_date,
          })
          .toPromise()

        if (
          response.data?.vaxiom_persons_aggregate.aggregate.count ||
          response.data?.leads_leads_aggregate.aggregate.count
        ) {
          setFlowTo(normalizePath(props.viewPath, './SimilarPatients/Content'))
          return
        }
      }

      let { person_referrals, template_referrals } = formatReferrals(
        value.patient_new.referrals
      )
      let patient_form = omitBy(
        {
          ...value.patient_new.person,
          // prettier-ignore
          ...(typeof value.patient_new?.provider_employee_resource_id === 'number'
            ? {
                provider_employee_resource_id:
                  value.patient_new.provider_employee_resource_id,
              }
            : {}),
          ...(person_referrals?.length > 0 ? { person_referrals } : {}),
          ...(template_referrals?.length > 0 ? { template_referrals } : {}),
        },
        isNil
      )

      mutationResponse = await executeMutationScheduleNewPatient({
        ...shared,
        unplanned: false,
        patient_form,
        lead_id: args.lead_id,
      })
    }

    if (mutationResponse.error) {
      notify(notifyError('Something went wrong'))
    } else {
      let new_patient
      if (!patient_id) {
        /**
         * NOTE: We need this extra query because Hasura doesn't properly return nested relationship data
         * from PostgreSQL functions, when they are freshly created, returning null for the appointment object
         * despite the relationship existing. We should revisit this later to find a more elegant solution.
         */
        let newPatientQueryResponse = await client
          .query(queryNewPatient, {
            appointment_booking_id:
              mutationResponse.data.calendar_scheduling_admin_for_new_patient
                ._id,
          })
          .toPromise()

        if (newPatientQueryResponse.data) {
          new_patient =
            newPatientQueryResponse.data.vaxiom_appointment_bookings?.[0]
              ?.appointment?.patient
        }
      }

      let action =
        !value.initial_location || value.initial_location?.id === location_id
          ? null
          : {
              text: `Switch back to last location`,
              onClick: () => {
                submitTab({
                  type: 'setLocation',
                  location_id: value.initial_location.id,
                  vaxiom_location_id: value.initial_location.vaxiom_id,
                  time_zone_id:
                    value.initial_location.time_zone_id || DEFAULT_TIMEZONE,
                  location_name: value.initial_location.name,
                })
              },
            }

      notify(
        notifySuccess(
          'Appointment for this patient is scheduled and added to calendar.',
          is_emergency
            ? 'Emergency appointment scheduled!'
            : 'Appointment scheduled!',
          action
        )
      )
      await submitTab({ type: 'appointmentOverlay/close' })

      if (new_patient) {
        // This timeout helps us to ensure that the changes triggered by
        // appointmentOverlay/close, are completed before the new tab is opened
        setTimeout(() => {
          submitGlobal({
            type: 'tabs/add',
            tab: {
              id: new_patient._id,
              type: 'Patient',
              name: patientName(new_patient.person),
              tab: 'profile',
              viewPath: '/App/Account/Content/Patients',
              patient_id: new_patient._id,
            },
          })
        }, 500)
      }
    }
  }
}

/** @type {import('Simple/types.js').useDataOnSubmit} */
function useDataOnActionUpdateReferral(props) {
  return async function onAction({ originalValue, change, args }) {
    change(next => {
      next.patient_new.referrals = args.referrals
    })
  }
}

/** @type {import('Simple/types.js').useDataOnSubmit} */
function useDataOnActionCreatePatient(props) {
  let [, notify] = useNotifications()
  let setFlowTo = useSetFlowTo(props.viewPath)
  let [, executeMutationCreatePatient] = useMutation(mutationCreatePatient)
  let [, executeMutationCreateEmployeeProfessionalRelationship] = useMutation(
    mutationCreateEmployeeProfessionalRelationship
  )

  let current_location_id = useDataValue({
    viewPath: props.viewPath,
    context: 'tab',
    path: 'selected.vaxiom_location_id',
  })
  let parent_company_id = useDataValue({
    viewPath: props.viewPath,
    context: 'global',
    path: 'current_location.parent_company.id',
  })

  return async function onAction({ value, change, args }) {
    let { person_referrals, template_referrals } = formatReferrals(
      value.patient_new.referrals
    )
    let data = {
      patient: {
        organization_id: parent_company_id,
        primary_location_id: current_location_id,
        person_id: args.person_id,
        patient_locations: {
          data: {
            location_id: current_location_id,
          },
        },
        patient_person_referrals: {
          data: person_referrals,
        },
        patient_template_referrals: {
          data: template_referrals,
        },
        ...(value.patient_new.provider_employee_resource_id
          ? {
              professional_relationships: {
                data: {
                  dtype: 'emploProfessionalRelationship',
                  past: false,
                },
              },
            }
          : {}),
      },
    }

    let mutationNewPatientResponse = await executeMutationCreatePatient(data)

    if (mutationNewPatientResponse?.error) {
      notify(
        notifyError(
          'Something went wrong. Please, try again or contact support if the problem persists.'
        )
      )
      return
    }

    let patient_response =
      mutationNewPatientResponse.data?.insert_vaxiom_patients_one
    let professional_relationship =
      patient_response.professional_relationships?.[0]

    // This has to be a separate mutations because of this bug
    // https://github.com/hasura/graphql-engine/issues/8232

    if (value.patient_new.provider_employee_resource_id) {
      await executeMutationCreateEmployeeProfessionalRelationship({
        input: {
          provider_id: value.patient_new.provider_employee_resource_id,
          id: professional_relationship.id,
        },
      })
    }

    notify(notifySuccess('Patient registered'))

    change(next => {
      next.patient_id = patient_response._id
      next.patient_option = 'existing'
      next.skip_appointment_reset = true
    })
    setFlowTo(normalizePath(props.viewPath, './SimilarPatients/No'))

    return
  }
}
